/**
 * Pure helpers behind `documents/FolderExplorer` (P-40, D-204, D-207 Q-189/Q-190).
 * No React, unit-tested (`utils/__tests__/folderExplorerModel.test.ts`).
 *
 * The client only PRE-checks what a drop may do so the UI can hide a drop it
 * knows will be refused; the server stays the authority (system folders, the
 * customer boundary, loops and edit rights are all enforced there).
 */

export interface FolderLike {
  id: string;
  name: string;
  parent_folder_id: string | null;
  is_system?: boolean;
  organization_id?: string;
}

export interface FolderNode<F extends FolderLike> {
  folder: F;
  children: FolderNode<F>[];
}

/** Longest folder / file name the API accepts. */
export const MAX_NAME_LENGTH = 255;

const byFolder = <F extends FolderLike>(a: F, b: F): number =>
  (a.is_system === b.is_system ? 0 : a.is_system ? -1 : 1) || a.name.localeCompare(b.name, 'he');

/** Direct children of `parentId` (null = the customer's top level): system folders first, then by name. */
export function childFolders<F extends FolderLike>(folders: readonly F[], parentId: string | null): F[] {
  const ids = new Set(folders.map((f) => f.id));
  return folders
    // A folder whose parent is not in the list (not visible / deleted) is shown at the top level, never lost.
    .filter((f) => (parentId === null ? f.parent_folder_id === null || !ids.has(f.parent_folder_id) : f.parent_folder_id === parentId))
    .sort(byFolder);
}

/** The whole forest, roots = the customer's top level. Cycle-safe (a corrupt loop is simply cut). */
export function buildFolderTree<F extends FolderLike>(folders: readonly F[]): FolderNode<F>[] {
  const seen = new Set<string>();
  const build = (parentId: string | null): FolderNode<F>[] =>
    childFolders(folders, parentId)
      .filter((f) => !seen.has(f.id))
      .map((f) => {
        seen.add(f.id);
        return { folder: f, children: build(f.id) };
      });
  return build(null);
}

/** Root -> folder chain for the breadcrumbs. [] for the top level or an unknown id. Cycle-safe. */
export function folderPath<F extends FolderLike>(folders: readonly F[], folderId: string | null): F[] {
  if (!folderId) return [];
  const byId = new Map(folders.map((f) => [f.id, f] as const));
  const out: F[] = [];
  const seen = new Set<string>();
  let cur = byId.get(folderId);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = cur.parent_folder_id ? byId.get(cur.parent_folder_id) : undefined;
  }
  return out;
}

/** `folderId` and everything below it. */
export function subtreeIds<F extends FolderLike>(folders: readonly F[], folderId: string): Set<string> {
  const out = new Set<string>([folderId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const f of folders) {
      if (f.parent_folder_id && out.has(f.parent_folder_id) && !out.has(f.id)) {
        out.add(f.id);
        grew = true;
      }
    }
  }
  return out;
}

export type DropRefusal =
  | 'other-customer'
  | 'system-folder'
  | 'into-self'
  | 'into-descendant'
  | 'same-place'
  | 'signed-file'
  | 'file-to-top';

export type DropVerdict = { ok: true } | { ok: false; reason: DropRefusal };

export interface DropTarget {
  organizationId: string;
  /** null = the customer's top level. */
  folderId: string | null;
}

/** May this folder be dropped on `target`? */
export function canDropFolder<F extends FolderLike>(
  folders: readonly F[],
  dragged: { id: string; organizationId: string },
  target: DropTarget,
): DropVerdict {
  if (dragged.organizationId !== target.organizationId) return { ok: false, reason: 'other-customer' };
  const f = folders.find((x) => x.id === dragged.id);
  if (!f) return { ok: false, reason: 'same-place' };
  if (f.is_system) return { ok: false, reason: 'system-folder' };
  if (target.folderId === f.id) return { ok: false, reason: 'into-self' };
  if (target.folderId && subtreeIds(folders, f.id).has(target.folderId)) return { ok: false, reason: 'into-descendant' };
  if ((f.parent_folder_id ?? null) === target.folderId) return { ok: false, reason: 'same-place' };
  return { ok: true };
}

/**
 * May this file be dropped on `target`? FIX-B21 (D-214): a file may go to the
 * customer's top level (QX-190), a signed / locked file may be moved (QX-192;
 * it is still never renamed), and a system folder is a valid target (QX-193).
 */
export function canDropDocument(
  doc: { folder_id: string | null; organization_id: string; status?: string },
  target: DropTarget,
): DropVerdict {
  if (doc.organization_id !== target.organizationId) return { ok: false, reason: 'other-customer' };
  if ((doc.folder_id ?? null) === target.folderId) return { ok: false, reason: 'same-place' };
  return { ok: true };
}

/** A signed / locked file keeps its name and content; it may only be moved (QX-192). */
export const isFrozenDocument = (doc: { status?: string }): boolean => doc.status === 'signed' || doc.status === 'locked';

/**
 * FIX-B42 (FQ-059): a copy the system made (a ticket file mirror, a quote PDF) is not a draft anyone works on, so its
 * "draft" status is not shown (the stored status stays - §41: a document is never CREATED sent / signed / locked).
 */
export const isSystemCopyDocument = (doc: { description?: string | null }): boolean =>
  typeof doc.description === 'string' && (doc.description.startsWith('ticket_attachment:') || doc.description.startsWith('quote_pdf:'));
export const shownDocumentStatus = (doc: { status?: string; description?: string | null }): string | undefined =>
  doc.status === 'draft' && isSystemCopyDocument(doc) ? undefined : doc.status;

/**
 * The folders of one customer as rows for `ui/EntityPicker` (FIX-B21, P-41
 * completion): tree order (system folders first, then by name) with `depth`, so
 * the picker indents children and shows the parents' path when searching.
 */
export function folderPickerOptions<F extends FolderLike>(folders: readonly F[]): Array<{ value: string; label: string; depth: number }> {
  const out: Array<{ value: string; label: string; depth: number }> = [];
  const walk = (nodes: FolderNode<F>[], depth: number) => {
    for (const n of nodes) {
      out.push({ value: n.folder.id, label: n.folder.name, depth });
      walk(n.children, depth + 1);
    }
  };
  walk(buildFolderTree(folders), 0);
  return out;
}

/** Lower-case, no Hebrew points / punctuation, single spaces: the same matching the customer picker uses. */
export function normalizeQuery(s: string): string {
  return s
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[֑-ׇ]/g, '')
    .replace(/["'`׳״.,\-_/\\()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const nameMatches = (name: string, query: string): boolean => {
  const q = normalizeQuery(query);
  if (!q) return true;
  const n = normalizeQuery(name);
  return q.split(' ').every((w) => n.includes(w));
};

/**
 * Tree search: the folders whose name matches, plus every ancestor (so a match
 * is shown in its place), plus the ancestors' ids as the set to auto-expand.
 */
export function searchFolders<F extends FolderLike>(
  folders: readonly F[],
  query: string,
): { visible: Set<string>; expand: Set<string>; matches: Set<string> } {
  const visible = new Set<string>();
  const expand = new Set<string>();
  const matches = new Set<string>();
  if (!normalizeQuery(query)) {
    for (const f of folders) visible.add(f.id);
    return { visible, expand, matches };
  }
  for (const f of folders) {
    if (!nameMatches(f.name, query)) continue;
    matches.add(f.id);
    for (const a of folderPath(folders, f.id)) {
      visible.add(a.id);
      if (a.id !== f.id) expand.add(a.id);
    }
  }
  return { visible, expand, matches };
}

export type NameProblem = 'empty' | 'too-long';

export function validateName(raw: string): NameProblem | null {
  const n = raw.trim();
  if (!n) return 'empty';
  if (n.length > MAX_NAME_LENGTH) return 'too-long';
  return null;
}

/** The drag payload (also kept in a ref, because `dragover` cannot read `dataTransfer`). */
export interface DragItem {
  kind: 'folder' | 'document';
  id: string;
  organizationId: string;
}

export const DRAG_MIME = 'application/x-ote-explorer-item';
